import RAPIER from '@dimforge/rapier3d-compat';
import type { Collider, RigidBody, World } from '@dimforge/rapier3d-compat';

import { WALL } from '../../game/constants';
import type { MazeLayout } from '../../game/mazeLayout';
import { quatFromAxisAngle } from '../../utils/math';

/**
 * Build the maze's collision geometry.
 *
 * Every collider is created in the maze body's LOCAL frame - i.e. exactly as if
 * the board were at rotation 0 - and attached to the kinematic body. The
 * rotation is NEVER baked into a collider's local transform; the body rotates
 * and takes all of them with it.
 *
 * Design notes:
 *  - Rings are tessellated into cuboids, not trimeshes. Rapier's docs warn that
 *    trimeshes are one-sided and volume-less, which is unstable for a moving
 *    body. Cuboids are robust and cheap.
 *  - Every collider built here corresponds to geometry the board model draws,
 *    and vice versa. The layout comes from `blender/spec/maze.json`, which the
 *    Blender build reads too, so the two cannot drift.
 *  - The outer guard is collided PER ARC, never as a closed ring. An arc's end
 *    IS the exit; a proxy that closed the circle would be an invisible wall in
 *    the one place where it is fatal, because the ball could never leave.
 *  - Scoring pockets are NOT sensors. A pocket is resolved from the ball's X as
 *    it crosses the row's mouth, by `src/game/scoreRow.ts`, so no collider
 *    participates in scoring and no mesh name or colour is ever consulted.
 */
export function buildMazeColliders(
  world: World,
  body: RigidBody,
  layout: MazeLayout,
): Collider[] {
  const colliders: Collider[] = [];
  const board = layout.board;

  // Cylinders in Rapier are Y-aligned by default; rotate onto the Z axis.
  const toZAxis = quatFromAxisAngle(1, 0, 0, -Math.PI / 2);

  // Backing plate - the ball rolls against its front face at z = 0.
  colliders.push(
    world.createCollider(
      RAPIER.ColliderDesc.cylinder(board.plateThickness / 2, board.plateRadius)
        .setTranslation(0, 0, -board.plateThickness / 2)
        .setRotation(toZAxis)
        .setFriction(WALL.friction)
        .setRestitution(WALL.restitution),
      body,
    ),
  );

  // Transparent front cover - caps the channel so the ball cannot pop out.
  colliders.push(
    world.createCollider(
      RAPIER.ColliderDesc.cylinder(board.coverThickness / 2, board.plateRadius)
        .setTranslation(0, 0, board.coverZ)
        .setRotation(toZAxis)
        .setFriction(WALL.friction)
        .setRestitution(WALL.restitution),
      body,
    ),
  );

  // Centre hub.
  colliders.push(
    world.createCollider(
      RAPIER.ColliderDesc.cylinder(board.hubHeight / 2, board.hubRadius)
        .setTranslation(0, 0, board.hubHeight / 2)
        .setRotation(toZAxis)
        .setFriction(WALL.friction)
        .setRestitution(WALL.restitution),
      body,
    ),
  );

  // Wall boxes: rings, guard arcs, radial walls, deflectors and ramp steps.
  for (const segment of layout.segments) {
    colliders.push(
      world.createCollider(
        RAPIER.ColliderDesc.cuboid(
          segment.length / 2,
          segment.thickness / 2,
          segment.depth / 2,
        )
          .setTranslation(segment.x, segment.y, segment.z)
          .setRotation(quatFromAxisAngle(0, 0, 1, segment.angleRad))
          .setFriction(WALL.friction)
          .setRestitution(WALL.restitution),
        body,
      ),
    );
  }

  // Pegs. A cylinder primitive, so these need no tessellation.
  for (const obstacle of layout.obstacles) {
    colliders.push(
      world.createCollider(
        RAPIER.ColliderDesc.cylinder(obstacle.height / 2, obstacle.radius)
          .setTranslation(obstacle.x, obstacle.y, obstacle.z)
          .setRotation(toZAxis)
          .setFriction(WALL.friction)
          .setRestitution(WALL.restitution),
        body,
      ),
    );
  }

  return colliders;
}
